import type { Job } from "providers/HostedScrapingProvider";
import { API_HOST } from "api";
import { estimateJobRunTime } from "components/hosted-scraping/estimateJobRunTime";
import { isEnterprise } from "utils/planUtils";
import { HOUR } from "utils/timeConstants";
import { ensureDate } from "utils/dateUtils";
import { BillingPath } from "v2/pages/billing/BillingPath";


export type PlaceHolderJobToMarkQueuedJob = Omit<Job, 'status'> & { status: Job['status'] | 'queued' };

export const lineClassName = 'font-normal min-h-16 py-2 px-1 text-sm text-gray dark:text-neutral-600 flex items-center';

export const hasResult = (job: Job): boolean => Boolean(job.result?.doSpaces?.resultKey);

export const isJobActive = (job: { status: string }): boolean => job.status === 'running' || job.status === 'cancelling';

export const resultsExpired = (job: Pick<Job, 'resultsExpireAt'>): boolean => {
  const expiresAt = ensureDate(job.resultsExpireAt ?? undefined);
  return expiresAt !== undefined && expiresAt.getTime() <= Date.now();
};

export const jobResultUrl = (publicJobId: string): string => `${API_HOST}/hostedscraping/job/${publicJobId}/result`;

export const jobErrorReportUrl = (publicJobId: string): string => `${API_HOST}/hostedscraping/job/${publicJobId}/errorreport`;

export const resultFormatLabel = (job: Job): string => {
  switch (job.result?.doSpaces?.format) {
    case 'zip-csv': return 'CSV';
    case 'zip-mk': return 'MKD';
    case 'zip-txt': return 'TXT';
    default: return 'JSON';
  }
};

// Older jobs keep the report on the job row, newer ones in DO Spaces.
export const hasErrorReport = (job: Job): boolean => {
  const inlineReport = job.result?.failureReport;
  return (Array.isArray(inlineReport) && inlineReport.length > 0) || Boolean(job.result?.failureReportDOSpacesKey);
};

export const jobErrorMessageToHumanReadable = (errorMessage: Job['result']['errorMessage']): { message: string, fixRelatedMessage?: string, fixUrl?: string } | null => {
  switch (errorMessage) {
    case "insufficient_credits":
      return {
        message: "This job was created, but it did not run. You didn't have enough credits to run it. ",
        fixRelatedMessage: "Upgrade your plan or enable Pay-as-you-go and retry.",
        fixUrl: BillingPath
      };
    default:
      return null;
  }
}

/*
 * Jobs with failed tasks from before the termination-reason rollout (and those whose failures
 * never carried a reason slug) have no terminationReasonStats, so they show no issue banner at
 * all. They do still have an error report, so we point those at the existing error-report view
 * instead. This is keyed on failed tasks, not job status: a 'done' job with some failures
 * ("Partially completed") gets the banner on the new path, so the fallback matches that.
 */
export const shouldShowFailureReportFallback = (job: Job | PlaceHolderJobToMarkQueuedJob): boolean => {
  if (job.failedTasks === 0) {
    return false;
  }
  // Only when there are no structured reasons at all. A job whose reasons are present but all
  // filtered from display (HIDDEN_TERMINATION_SLUGS) stays hidden — that omission is deliberate.
  const stats = job.terminationReasonStats;
  if (stats !== null && stats !== undefined && Object.keys(stats).length > 0) {
    return false;
  }
  // Jobs that explain themselves inline (insufficient_credits) keep just that message.
  if (jobErrorMessageToHumanReadable(job.result?.errorMessage) !== null) {
    return false;
  }

  // Legacy jobs stored the report on the job row itself; it has no expiry.
  const inlineReport = job.result?.failureReport;
  if (Array.isArray(inlineReport) && inlineReport.length > 0) {
    return true;
  }
  if (!job.result?.failureReportDOSpacesKey) {
    return false;
  }
  // The stored report is deleted once the job's results expire, so the link would come back empty.
  return !resultsExpired(job);
}

export const jobInfoMessageAreaVisible = (job: Job, planSlug: string | undefined, concurrencyLimit: number): { jobInfoVisible: boolean; remainingTotalSeconds: number | undefined, progressPercentage: number | undefined } => {
  if (!isJobActive(job)) {
    return { jobInfoVisible: false, remainingTotalSeconds: undefined, progressPercentage: undefined };
  }

  const totalJobsCount = job.successfulTasks + job.failedTasks + job.inProgressTasks;
  const finishedJobsCount = job.successfulTasks + job.failedTasks;
  const { remainingTotalSeconds, totalEstimatedSeconds, progressPercentage } = estimateJobRunTime(concurrencyLimit, job.startedAt ?? job.createdAt, totalJobsCount, finishedJobsCount, job.aggregationProgress?.finished);

  const isJobInfoVisible =
    !isEnterprise(planSlug)  // Don't beg for enterprise customers to upgrade
    && totalEstimatedSeconds !== undefined  // Don't show if we are not sure
    && totalEstimatedSeconds * 1000 >= HOUR  // Don't show it for slow jobs
    && job.inProgressTasks > 0; // Don't show in the finalizing state

  return { jobInfoVisible: isJobInfoVisible, remainingTotalSeconds, progressPercentage };
}

